import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Switch } from '../ui/switch';
import {
  Menu, X, Clock, Phone, Mail, MapPin, Users, Sparkles, ChevronDown,
  Facebook, Instagram, Linkedin, Youtube, Twitter, Home, Timer, Flame, Rocket,
  PenTool, Video, Globe, Megaphone, Crosshair, ChevronRight, ArrowRight,
  Download, Building, Trophy
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { APP_VERSION, BUILD_DATE } from '../../version';

// ============================================
// AGENCY SCHEDULE & STATUS
// ============================================
// Default schedule (fallback if API fails)
const DEFAULT_SCHEDULE = {
  regular: {
    1: { name: 'Lundi', hours: [{ open: '9h00', close: '18h00' }] },
    2: { name: 'Mardi', hours: [{ open: '9h00', close: '18h00' }] },
    3: { name: 'Mercredi', hours: [{ open: '9h00', close: '18h00' }] },
    4: { name: 'Jeudi', hours: [{ open: '9h00', close: '18h00' }] },
    5: { name: 'Vendredi', hours: [{ open: '9h00', close: '18h00' }] },
    6: { name: 'Samedi', hours: [] },
    0: { name: 'Dimanche', hours: [] },
  },
  special: []
};

// Convert API schedule format to internal format
const convertScheduleFromApi = (apiSchedule) => {
  if (!apiSchedule?.regular) return DEFAULT_SCHEDULE;
  
  const dayMapping = {
    'monday': 1, 'tuesday': 2, 'wednesday': 3, 'thursday': 4,
    'friday': 5, 'saturday': 6, 'sunday': 0
  };
  
  const dayNames = {
    1: 'Lundi', 2: 'Mardi', 3: 'Mercredi', 4: 'Jeudi',
    5: 'Vendredi', 6: 'Samedi', 0: 'Dimanche'
  };
  
  const converted = { regular: {}, special: [] };
  
  Object.entries(apiSchedule.regular).forEach(([dayName, data]) => {
    const dayNum = dayMapping[dayName];
    if (dayNum !== undefined) {
      if (data.closed || !data.open) {
        converted.regular[dayNum] = { name: dayNames[dayNum], hours: [] };
      } else {
        // Handle half-day format
        const hours = [];
        if (data.morning_open && data.morning_close) {
          hours.push({ open: data.morning_open, close: data.morning_close });
        }
        if (data.afternoon_open && data.afternoon_close) {
          hours.push({ open: data.afternoon_open, close: data.afternoon_close });
        }
        // Simple format (open/close)
        if (hours.length === 0 && data.open && data.close) {
          hours.push({ open: data.open, close: data.close });
        }
        converted.regular[dayNum] = { name: dayNames[dayNum], hours };
      }
    }
  });
  
  // Ensure all days exist
  for (let i = 0; i <= 6; i++) {
    if (!converted.regular[i]) {
      converted.regular[i] = DEFAULT_SCHEDULE.regular[i];
    }
  }
  
  return converted;
};

const DAYS_FR = {
  0: 'Dimanche', 1: 'Lundi', 2: 'Mardi', 3: 'Mercredi',
  4: 'Jeudi', 5: 'Vendredi', 6: 'Samedi'
};

export const AGENCY_CONTACT = {
  phone: "04 84 49 27 00",
  email: "contact@agence-msc.fr",
  address: "52 Rue des Pétouses",
  city: "13800 Istres"
};

const getAgencyStatus = (schedule = DEFAULT_SCHEDULE) => {
  const now = new Date();
  const day = now.getDay();
  const hours = now.getHours();
  const minutes = now.getMinutes();
  const currentTime = hours * 60 + minutes;
  
  const todaySchedule = schedule.regular[day];
  if (!todaySchedule || todaySchedule.hours.length === 0) {
    return { status: 'closed', label: 'Fermé', sublabel: 'Réouvre lundi', color: 'text-red-500', bg: 'bg-red-50', dot: 'bg-red-500' };
  }

  const sessions = todaySchedule.hours;
  for (const session of sessions) {
    const openHour = parseInt(session.open.split('h')[0]);
    const openMin = parseInt(session.open.split('h')[1] || '0');
    const closeHour = parseInt(session.close.split('h')[0]);
    const closeMin = parseInt(session.close.split('h')[1] || '0');
    const openTime = openHour * 60 + openMin;
    const closeTime = closeHour * 60 + closeMin;
    
    if (currentTime >= openTime && currentTime < closeTime) {
      return { 
        status: 'open', 
        label: 'Ouvert', 
        sublabel: `jusqu'à ${session.close}`,
        color: 'text-green-600', 
        bg: 'bg-green-50', 
        dot: 'bg-green-500 animate-pulse' 
      };
    }
  }
  
  // Check if in lunch break
  if (sessions.length > 1) {
    const morningClose = parseInt(sessions[0].close.split('h')[0]) * 60 + parseInt(sessions[0].close.split('h')[1] || '0');
    const afternoonOpen = parseInt(sessions[1].open.split('h')[0]) * 60 + parseInt(sessions[1].open.split('h')[1] || '0');
    if (currentTime >= morningClose && currentTime < afternoonOpen) {
      return { 
        status: 'break', 
        label: 'Pause repas', 
        sublabel: `réouvre à ${sessions[1].open}`,
        color: 'text-orange-500', 
        bg: 'bg-orange-50', 
        dot: 'bg-orange-500' 
      };
    }
  }

  return { status: 'closed', label: 'Fermé', sublabel: 'Réouvre demain', color: 'text-red-500', bg: 'bg-red-50', dot: 'bg-red-500' };
};

// Services for menu (fallback)
const DEFAULT_SERVICES = [
  { id: 'creation-graphique', shortTitle: 'Graphisme', icon: PenTool, color: 'from-pink-500 to-rose-600', description: 'Logos, chartes graphiques, supports print et digitaux' },
  { id: 'creation-video', shortTitle: 'Vidéo', icon: Video, color: 'from-purple-500 to-violet-600', description: 'Films corporate, motion design, captation événementielle' },
  { id: 'creation-web', shortTitle: 'Web', icon: Globe, color: 'from-blue-500 to-cyan-600', description: 'Sites vitrines, e-commerce, applications web' },
  { id: 'marketing-digital', shortTitle: 'Marketing', icon: Megaphone, color: 'from-orange-500 to-red-600', description: 'SEO, publicité en ligne, community management' },
];

// Icon mapping for dynamic services
const SERVICE_ICONS = {
  'creation-graphique': PenTool,
  'creation-video': Video,
  'creation-web': Globe,
  'marketing-digital': Megaphone,
  'hebergement': Globe,
  'default': Sparkles
};

// Color mapping for services (hex to tailwind gradient)
const SERVICE_COLORS = {
  'creation-graphique': 'from-pink-500 to-rose-600',
  'creation-video': 'from-purple-500 to-violet-600',
  'creation-web': 'from-blue-500 to-cyan-600',
  'marketing-digital': 'from-orange-500 to-red-600',
  'hebergement': 'from-amber-500 to-orange-600',
  'default': 'from-cyan-500 to-blue-600'
};

// ============================================
// SITE HEADER COMPONENT
// ============================================
export const SiteHeader = ({ transparent = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showMegaMenu, setShowMegaMenu] = useState(false);
  const [themeSettings, setThemeSettings] = useState(null);
  const [copiloteMode, setCopiloteMode] = useState(false);
  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
  const [agencyStatus, setAgencyStatus] = useState(getAgencyStatus(DEFAULT_SCHEDULE));
  const [cmsServices, setCmsServices] = useState(null);
  const [cmsLogos, setCmsLogos] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  
  const copiloteGradient = "from-orange-500 via-red-500 to-rose-600";
  
  // Get active services (from CMS or fallback)
  const activeServices = cmsServices || DEFAULT_SERVICES;
  
  // Determine if header should be in "light" mode (transparent background, white text)
  const isLightMode = transparent && !scrolled && !mobileMenuOpen;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load schedule from API
  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        const res = await api.get('/settings/theme');
        if (res.data?.schedule) {
          const convertedSchedule = convertScheduleFromApi(res.data.schedule);
          setSchedule(convertedSchedule);
          setAgencyStatus(getAgencyStatus(convertedSchedule));
        }
      } catch (e) {
        console.log('Schedule not loaded, using defaults');
      }
    };
    fetchSchedule();
    
    // Update status every minute
    const interval = setInterval(() => {
      setAgencyStatus(getAgencyStatus(schedule));
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const res = await api.get('/cms/logos');
        // Transform logos response to match expected themeSettings structure
        if (res.data) {
          // Helper function to fix URLs from old domains
          const fixImageUrl = (url) => {
            if (!url) return null;
            if (url.includes('/api/uploads/')) {
              const match = url.match(/\/api\/uploads\/[^\s"']+/);
              if (match) {
                return `${window.location.origin}${match[0]}`;
              }
            }
            if (url.startsWith('http')) return url;
            return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
          };
          
          setCmsLogos(res.data);
          setThemeSettings({
            logo: {
              lightUrl: res.data.logo_light ? fixImageUrl(res.data.logo_light) : null,
              darkUrl: res.data.logo_dark ? fixImageUrl(res.data.logo_dark) : null,
            },
            agency: {
              name: res.data.logo_alt || "MSC L'AGENCE"
            }
          });
        }
        
        // Also fetch client portal settings + site section toggles
        const siteSettingsRes = await api.get('/cms/site-settings').catch(() => ({ data: null }));
        if (siteSettingsRes.data) {
          setSiteSettings(siteSettingsRes.data);
          setThemeSettings(prev => ({
            ...prev,
            clientPortal: {
              url: siteSettingsRes.data.client_portal_url || '/login',
              label: siteSettingsRes.data.client_portal_label || 'Espace Client',
              openInNewTab: siteSettingsRes.data.client_portal_new_tab || false
            }
          }));
        }
      } catch (e) {
        console.log('Theme not loaded');
      }
    };
    fetchTheme();
  }, []);

  // Load services from CMS
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await api.get('/website/services');
        if (res.data?.length > 0) {
          // Helper function to fix URLs from old domains
          const fixImageUrl = (url) => {
            if (!url) return null;
            if (url.includes('/api/uploads/')) {
              const match = url.match(/\/api\/uploads\/[^\s"']+/);
              if (match) {
                return `${window.location.origin}${match[0]}`;
              }
            }
            if (url.startsWith('http')) return url;
            return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
          };
          
          const mappedServices = res.data.map(svc => ({
            id: svc.slug || svc.id,
            shortTitle: svc.short_name || svc.name || svc.title,
            description: svc.description || '',
            icon: SERVICE_ICONS[svc.slug] || SERVICE_ICONS['default'],
            color: SERVICE_COLORS[svc.slug] || SERVICE_COLORS['default'],
            mediaUrl: svc.media_url ? fixImageUrl(svc.media_url) : null,
            heroMedia: svc.hero_media ? {
              type: svc.hero_media.type,
              url: fixImageUrl(svc.hero_media.url)
            } : null,
          }));
          setCmsServices(mappedServices);
        }
      } catch (e) {
        console.log('Services not loaded');
      }
    };
    fetchServices();
  }, []);

  const scrollToSection = (id) => {
    if (location.pathname !== '/site') {
      navigate('/site');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <>
      <nav className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        mobileMenuOpen 
          ? "bg-white shadow-lg" 
          : scrolled 
            ? "bg-white/95 backdrop-blur-lg shadow-lg" 
            : transparent 
              ? "bg-transparent" 
              : "bg-white/95 backdrop-blur-lg shadow-sm"
      )}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo - CMS Priority */}
            <button 
              onClick={() => { navigate('/site'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="flex items-center z-50"
            >
              {copiloteMode ? (
                /* Logo COPILOTE */
                <div className="flex flex-col items-center group cursor-pointer">
                  <span 
                    className={cn(
                      "font-black text-2xl lg:text-3xl tracking-tight transition-colors",
                      scrolled || mobileMenuOpen 
                        ? "bg-gradient-to-r from-orange-500 via-amber-400 to-yellow-400 bg-clip-text text-transparent"
                        : isLightMode ? "text-white" : "bg-gradient-to-r from-orange-500 via-amber-400 to-yellow-400 bg-clip-text text-transparent"
                    )}
                    style={scrolled || mobileMenuOpen || !isLightMode ? {filter: 'drop-shadow(0 0 8px rgba(251, 146, 60, 0.3))'} : {}}
                  >
                    MSC
                  </span>
                  <span 
                    className={cn(
                      "text-[10px] lg:text-xs font-semibold -mt-1 tracking-[0.1em] uppercase transition-colors",
                      scrolled || mobileMenuOpen ? "text-orange-500" : isLightMode ? "text-white/80" : "text-orange-500"
                    )}
                  >
                    Copilote
                  </span>
                </div>
              ) : cmsLogos?.logo_light || cmsLogos?.logo_dark ? (
                /* Logo from CMS - PRIORITY */
                <div className="h-10 lg:h-12 flex items-center">
                  <img 
                    src={(() => {
                      // Helper function to fix URLs from old domains
                      const fixUrl = (url) => {
                        if (!url) return null;
                        if (url.includes('/api/uploads/')) {
                          const match = url.match(/\/api\/uploads\/[^\s"']+/);
                          if (match) return `${window.location.origin}${match[0]}`;
                        }
                        if (url.startsWith('http')) return url;
                        return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
                      };
                      // Use dark logo on light background (scrolled or not transparent)
                      const useDarkLogo = scrolled || mobileMenuOpen || !isLightMode;
                      const logoPath = useDarkLogo ? cmsLogos?.logo_light : cmsLogos?.logo_dark;
                      // Fallback to any available logo
                      const finalLogo = logoPath || cmsLogos?.logo_dark || cmsLogos?.logo_light;
                      return fixUrl(finalLogo);
                    })()}
                    alt={cmsLogos?.logo_alt || "MSC L'Agence"}
                    className={cn(
                      "h-full w-auto object-contain max-w-[100px] sm:max-w-[120px] lg:max-w-[150px] transition-all duration-300",
                      isLightMode && !scrolled && !mobileMenuOpen && "brightness-0 invert"
                    )}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              ) : themeSettings?.logo?.lightUrl ? (
                <div className="h-10 lg:h-12 rounded-lg flex items-center justify-center transition-all hover:scale-105">
                  <img 
                    src={isLightMode && themeSettings?.logo?.darkUrl ? themeSettings.logo.darkUrl : themeSettings.logo.lightUrl} 
                    alt={themeSettings.agency?.name || "Logo"} 
                    className={cn(
                      "h-full w-auto object-contain max-w-[120px] lg:max-w-[150px] transition-all duration-300",
                      isLightMode && !themeSettings?.logo?.darkUrl && "brightness-0 invert"
                    )}
                  />
                </div>
              ) : (
                /* Logo texte par défaut */
                <div className="flex flex-col items-center group cursor-pointer">
                  <span className={cn(
                    "font-black text-2xl lg:text-3xl tracking-tight bg-clip-text text-transparent",
                    isLightMode 
                      ? "bg-gradient-to-r from-white via-white to-white" 
                      : "bg-gradient-to-r from-orange-500 via-amber-400 to-yellow-400"
                  )} style={{filter: isLightMode ? 'none' : 'drop-shadow(0 0 8px rgba(251, 146, 60, 0.3))'}}>
                    MSC
                  </span>
                  <span className={cn(
                    "text-[10px] lg:text-xs font-semibold -mt-1 tracking-[0.1em] uppercase",
                    isLightMode ? "text-white/80" : "text-orange-500"
                  )}>
                    L&apos;Agence
                  </span>
                </div>
              )}
            </button>

            {/* Mobile: Agency Status */}
            <button 
              onClick={() => setShowScheduleModal(true)}
              className="flex lg:hidden items-center"
            >
              <div className={cn(
                "flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs transition-all",
                agencyStatus.bg,
                "hover:scale-105"
              )}>
                <Clock className={cn("w-3.5 h-3.5", agencyStatus.color)} />
                <span className={cn("w-2 h-2 rounded-full", agencyStatus.dot)} />
                <span className={cn("font-medium", agencyStatus.color)}>
                  {agencyStatus.label}
                </span>
              </div>
            </button>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-5">
              <button 
                onClick={() => { navigate('/site'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className={cn(
                  "w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110",
                  isLightMode 
                    ? "bg-white/20 text-white hover:bg-white hover:text-cyan-500" 
                    : "bg-slate-100 text-slate-700 hover:bg-cyan-500 hover:text-white"
                )}
                title="Accueil"
              >
                <Home className="w-4 h-4" />
              </button>
              
              {/* Services Mega Menu Trigger */}
              <div 
                className="relative"
                onMouseEnter={() => setShowMegaMenu(true)}
                onMouseLeave={() => setShowMegaMenu(false)}
              >
                <button className={cn(
                  "flex items-center gap-1 font-medium transition-colors py-2",
                  isLightMode ? "text-white hover:text-cyan-300" : "text-slate-700 hover:text-cyan-500"
                )}>
                  Services
                  <ChevronDown className={cn("w-4 h-4 transition-transform", showMegaMenu && "rotate-180")} />
                </button>

                {showMegaMenu && (
                  <>
                    <div className="absolute left-1/2 -translate-x-1/2 top-full w-[900px] h-6 bg-transparent" />
                    <div className="fixed top-20 left-1/2 -translate-x-1/2 w-[900px] z-50">
                      <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden">
                        <div className="p-6">
                          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-5 text-center">Nos expertises</p>
                          <div className="grid grid-cols-4 gap-3">
                            {activeServices.map((service) => (
                              <Link
                                key={service.id}
                                to={`/site/${service.id}`}
                                className="group relative overflow-hidden rounded-xl bg-slate-50 hover:bg-slate-100 transition-all"
                                onClick={() => setShowMegaMenu(false)}
                              >
                                {/* Media Banner */}
                                <div className={cn("h-24 relative overflow-hidden bg-gradient-to-br", service.color)}>
                                  {/* Background media from heroMedia or mediaUrl */}
                                  {service.heroMedia?.type === 'video' ? (
                                    <video 
                                      autoPlay 
                                      loop 
                                      muted 
                                      playsInline
                                      className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 transition-opacity"
                                    >
                                      <source src={service.heroMedia.url} type="video/mp4" />
                                    </video>
                                  ) : service.heroMedia?.type === 'image' || service.mediaUrl ? (
                                    <img 
                                      src={service.heroMedia?.url || service.mediaUrl} 
                                      alt=""
                                      className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 transition-opacity"
                                    />
                                  ) : null}
                                  {/* Gradient overlay */}
                                  <div className={cn("absolute inset-0 bg-gradient-to-br opacity-70", service.color)} />
                                  {/* Icon */}
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                                      <service.icon className="w-6 h-6 text-white" />
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Service Info */}
                                <div className="p-3">
                                  <h4 className="font-semibold text-slate-800 text-sm group-hover:text-cyan-600 transition-colors">
                                    {service.shortTitle}
                                  </h4>
                                  {service.description && (
                                    <p className="text-xs text-slate-500 line-clamp-2 mt-1">{service.description.slice(0, 60)}...</p>
                                  )}
                                </div>
                              </Link>
                            ))}
                          </div>
                        </div>
                        
                        {/* COPILOTE Banner */}
                        <div className="mx-6 mb-6 rounded-xl overflow-hidden relative bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800">
                          <div className="absolute inset-0 opacity-10">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full blur-3xl" />
                            <div className="absolute bottom-0 left-1/4 w-24 h-24 bg-cyan-400 rounded-full blur-2xl" />
                          </div>
                          <div className="relative flex items-center justify-between p-5">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur flex items-center justify-center">
                                <Rocket className="w-6 h-6 text-cyan-400" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-white text-lg">COPILOTE</span>
                                  <span className="px-2 py-0.5 rounded-full bg-cyan-400/20 text-cyan-300 text-xs font-medium">Premium</span>
                                </div>
                                <p className="text-slate-300 text-sm">Pilotage marketing avec ROI garanti • +150% performance</p>
                              </div>
                            </div>
                            <Link 
                              to="/site#contact"
                              onClick={() => setShowMegaMenu(false)}
                              className="flex items-center gap-2 rounded-full px-6 py-2.5 bg-gradient-to-r from-orange-500 via-red-500 to-rose-600 text-white font-medium shadow-lg hover:shadow-xl transition-shadow"
                            >
                              <Crosshair className="w-4 h-4" />
                              Lancer une mission
                            </Link>
                          </div>
                        </div>
                        
                        {/* Bottom Bar */}
                        <div className="px-6 py-4 bg-slate-50 border-t flex items-center justify-between">
                          <div className="flex items-center gap-6">
                            <a href={`tel:${AGENCY_CONTACT.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 text-slate-600 hover:text-cyan-600 transition-colors text-sm">
                              <Phone className="w-4 h-4" /> {AGENCY_CONTACT.phone}
                            </a>
                            <a href={`mailto:${AGENCY_CONTACT.email}`} className="flex items-center gap-2 text-slate-600 hover:text-cyan-600 transition-colors text-sm">
                              <Mail className="w-4 h-4" /> {AGENCY_CONTACT.email}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <Link
                to="/site/realisations"
                className={cn(
                  "font-medium transition-colors",
                  isLightMode 
                    ? "text-white hover:text-cyan-300" 
                    : isActive('/site/realisations') ? "text-cyan-500" : "text-slate-700 hover:text-cyan-500"
                )}
              >
                Réalisations
              </Link>
              <Link
                to="/site/equipe"
                className={cn(
                  "font-medium transition-colors",
                  isLightMode 
                    ? "text-white hover:text-cyan-300" 
                    : isActive('/site/equipe') ? "text-cyan-500" : "text-slate-700 hover:text-cyan-500"
                )}
              >
                L&apos;équipe
              </Link>
              <button
                onClick={() => scrollToSection('contact')}
                className={cn(
                  "font-medium transition-colors",
                  isLightMode ? "text-white hover:text-cyan-300" : "text-slate-700 hover:text-cyan-500"
                )}
              >
                Contact
              </button>

              {/* Agency Status Badge */}
              <button 
                onClick={() => setShowScheduleModal(true)}
                className={cn(
                  "flex items-center gap-2 px-3 py-1.5 rounded-full text-sm hover:scale-105 transition-all cursor-pointer",
                  agencyStatus.bg
                )}
              >
                <span className={cn("w-2 h-2 rounded-full", agencyStatus.dot)} />
                <span className={cn("font-medium", agencyStatus.color)}>
                  {agencyStatus.label}
                </span>
                <span className="text-slate-400 text-xs hidden xl:inline">
                  {agencyStatus.sublabel}
                </span>
              </button>

              {/* COPILOTE Button - Only when copilote section enabled */}
              {siteSettings?.copilote_section?.enabled && (
              <button
                onClick={() => setCopiloteMode(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-full border transition-all cursor-pointer bg-gradient-to-r from-orange-500 to-red-600 border-transparent shadow-lg shadow-orange-500/30 hover:shadow-xl hover:scale-105"
              >
                <Flame className="w-4 h-4 text-white" />
                <span className="text-sm font-semibold text-white">COPILOTE</span>
              </button>
              )}

              {/* Nos Outils */}
              <Link
                to="/site/nos-outils"
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-full border transition-all cursor-pointer",
                  scrolled || !isLightMode
                    ? "bg-gradient-to-r from-cyan-500 to-blue-600 border-transparent shadow-lg shadow-cyan-500/20 hover:shadow-xl hover:scale-105 text-white"
                    : "bg-white/10 backdrop-blur-sm border-white/30 hover:bg-white/20 text-white"
                )}
                data-testid="shared-header-nos-outils-btn"
              >
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-semibold">Nos Outils</span>
              </Link>
            </div>

            {/* CTA Buttons with Phone */}
            <div className="hidden lg:flex items-center gap-3">
              {/* Phone - Full on desktop */}
              <a 
                href="tel:0484492700" 
                className={cn(
                  "flex items-center gap-2 font-medium transition-colors",
                  scrolled ? "text-slate-700 hover:text-cyan-600" : isLightMode ? "text-white hover:text-cyan-300" : "text-slate-700 hover:text-cyan-600"
                )}
              >
                <Phone className="w-5 h-5" />
                <span className="hidden md:inline">04 84 49 27 00</span>
              </a>
              
              <button 
                onClick={() => {
                  const url = themeSettings?.clientPortal?.url || '/login';
                  if (themeSettings?.clientPortal?.openInNewTab) {
                    window.open(url, '_blank');
                  } else {
                    window.location.href = url;
                  }
                }}
                className="h-9 px-6 rounded-full bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-medium transition-colors"
              >
                {themeSettings?.clientPortal?.label || 'Espace Client'}
              </button>
            </div>

            {/* Mobile: Account + Phone + Menu Button */}
            <div className="flex lg:hidden items-center gap-0.5">
              {/* Account icon - Opens Espace Client */}
              <button 
                onClick={() => {
                  const url = themeSettings?.clientPortal?.url || '/login';
                  if (themeSettings?.clientPortal?.openInNewTab) {
                    window.open(url, '_blank');
                  } else {
                    window.location.href = url;
                  }
                }}
                className={cn(
                  "p-2.5 rounded-full transition-all",
                  scrolled || mobileMenuOpen 
                    ? "text-slate-700 hover:bg-slate-100" 
                    : isLightMode ? "text-white hover:bg-white/20" : "text-slate-700 hover:bg-slate-100"
                )}
              >
                <Users className="w-7 h-7" />
              </button>
              
              {/* Phone icon */}
              <a 
                href="tel:0484492700" 
                className={cn(
                  "p-2.5 rounded-full transition-all",
                  scrolled || mobileMenuOpen 
                    ? "text-cyan-600 hover:bg-cyan-50" 
                    : isLightMode ? "text-white hover:bg-white/20" : "text-cyan-600 hover:bg-cyan-50"
                )}
              >
                <Phone className="w-7 h-7" />
              </a>
              
              {/* Menu Button with animation */}
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={cn(
                  "p-2.5 z-50 rounded-full transition-all",
                  scrolled || mobileMenuOpen 
                    ? "text-slate-800 hover:bg-slate-100" 
                    : isLightMode ? "text-white hover:bg-white/20" : "text-slate-800 hover:bg-slate-100"
                )}
              >
                <div className="relative w-7 h-7">
                  <X className={cn(
                    "w-7 h-7 absolute inset-0 transition-all duration-300",
                    mobileMenuOpen ? "opacity-100 rotate-0" : "opacity-0 rotate-90"
                  )} />
                  <Menu className={cn(
                    "w-7 h-7 absolute inset-0 transition-all duration-300",
                    mobileMenuOpen ? "opacity-0 -rotate-90" : "opacity-100 rotate-0"
                  )} />
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t shadow-lg max-h-[80vh] overflow-y-auto">
            <div className="p-4 pb-8 space-y-2">
              {/* Quick Actions - Equal height buttons with rounded corners */}
              <div className="flex gap-3 mb-4">
                <button 
                  onClick={() => { setMobileMenuOpen(false); window.open(`${process.env.REACT_APP_BACKEND_URL}/api/website/brochure/download`, '_blank'); }} 
                  className="flex-1 flex items-center justify-center gap-2 h-12 bg-cyan-500 hover:bg-cyan-600 text-white rounded-full font-medium transition-colors"
                >
                  <Download className="w-5 h-5" /> Brochure
                </button>
                <button 
                  onClick={() => { setMobileMenuOpen(false); scrollToSection('contact'); }} 
                  className="flex-1 flex items-center justify-center gap-2 h-12 bg-slate-800 hover:bg-slate-900 text-white rounded-full font-medium transition-colors"
                >
                  Devis gratuit
                </button>
              </div>

              {/* Agency Status in menu */}
              <button
                onClick={() => { setMobileMenuOpen(false); setShowScheduleModal(true); }}
                className={cn(
                  "w-full flex items-center justify-center gap-2 py-2.5 rounded-full mb-3",
                  agencyStatus.bg
                )}
              >
                <Clock className={cn("w-4 h-4", agencyStatus.color)} />
                <span className={cn("w-2 h-2 rounded-full", agencyStatus.dot)} />
                <span className={cn("font-medium text-sm", agencyStatus.color)}>
                  {agencyStatus.label}
                </span>
                <span className="text-slate-400 text-xs">
                  • {agencyStatus.sublabel}
                </span>
              </button>

              {/* Navigation Links */}
              <Link to="/site" className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-slate-50" onClick={() => setMobileMenuOpen(false)}>
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Building className="w-4 h-4 text-slate-600" />
                </div>
                <span className="font-medium">Accueil</span>
              </Link>
              
              {/* Services */}
              <div className="py-2 space-y-1">
                <p className="px-3 text-xs font-semibold text-slate-400 uppercase mb-2">Services</p>
                {activeServices.map(service => (
                  <Link
                    key={service.id}
                    to={`/site/${service.id}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50"
                  >
                    <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br", service.color)}>
                      <service.icon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-slate-700">{service.shortTitle}</span>
                  </Link>
                ))}
              </div>
              
              <div className="space-y-1">
                <Link 
                  to="/site/realisations" 
                  onClick={() => setMobileMenuOpen(false)} 
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg",
                    isActive('/site/realisations') ? "bg-cyan-50 text-cyan-600" : "hover:bg-slate-50 text-slate-700"
                  )}
                >
                  <span className="font-medium">Réalisations</span>
                </Link>
                
                <Link 
                  to="/site/equipe" 
                  onClick={() => setMobileMenuOpen(false)} 
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg",
                    isActive('/site/equipe') ? "bg-cyan-50 text-cyan-600" : "hover:bg-slate-50 text-slate-700"
                  )}
                >
                  <span className="font-medium">L&apos;équipe</span>
                </Link>
                
                <Link 
                  to="/site/blog" 
                  onClick={() => setMobileMenuOpen(false)} 
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg",
                    isActive('/site/blog') ? "bg-cyan-50 text-cyan-600" : "hover:bg-slate-50 text-slate-700"
                  )}
                >
                  <span className="font-medium">Blog</span>
                </Link>
                
                <button 
                  onClick={() => { scrollToSection('contact'); setMobileMenuOpen(false); }} 
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-slate-700 w-full text-left"
                >
                  <Mail className="w-5 h-5 text-slate-500" />
                  <span className="font-medium">Contact</span>
                </button>
              </div>

              {/* COPILOTE Mode - Only when copilote section enabled */}
              {siteSettings?.copilote_section?.enabled && (
              <div className="p-4 rounded-xl mt-2 bg-gradient-to-r from-orange-500/20 to-red-600/20 border border-orange-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Flame className="w-5 h-5 text-orange-400" />
                  <span className="font-bold text-orange-400">COPILOTE MSC</span>
                </div>
                <p className="text-sm mb-3 text-slate-600">Accompagnement stratégique sur 12 mois</p>
                <Link
                  to="/site/copilote"
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-orange-500 to-red-600 text-white font-semibold rounded-lg"
                >
                  <Crosshair className="w-4 h-4" />
                  Candidater au programme
                </Link>
              </div>
              )}

              {/* Nos Outils - Mobile */}
              <Link
                to="/site/nos-outils"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 p-4 rounded-xl mt-2 bg-gradient-to-r from-cyan-500/10 to-blue-600/10 border border-cyan-500/20"
              >
                <Sparkles className="w-5 h-5 text-cyan-500" />
                <div>
                  <span className="font-bold text-cyan-600">Nos Outils</span>
                  <p className="text-xs text-slate-500">Applications développées par MSC</p>
                </div>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Schedule Modal */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-cyan-500" />
              Horaires d&apos;ouverture
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-4">
            {Object.entries(schedule.regular).map(([dayNum, dayData]) => {
              const isToday = new Date().getDay() === parseInt(dayNum);
              return (
                <div 
                  key={dayNum} 
                  className={cn(
                    "flex justify-between py-3 px-4 rounded-lg",
                    isToday ? "bg-cyan-50 border border-cyan-200" : "bg-slate-50"
                  )}
                >
                  <div className="flex items-center gap-2">
                    {isToday && <span className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse" />}
                    <span className={cn("font-medium", isToday ? "text-cyan-600" : "text-slate-700")}>
                      {dayData.name}
                    </span>
                  </div>
                  <span className={cn("text-sm", dayData.hours.length === 0 ? "text-slate-400" : "text-slate-600")}>
                    {dayData.hours.length === 0 
                      ? 'Fermé' 
                      : dayData.hours.map(h => `${h.open} - ${h.close}`).join(' / ')}
                  </span>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ============================================
// SITE FOOTER COMPONENT
// ============================================
export const SiteFooter = () => {
  const [themeSettings, setThemeSettings] = useState(null);
  const [agencyInfo, setAgencyInfo] = useState(null);
  const [cmsServices, setCmsServices] = useState(null);
  const [socialLinks, setSocialLinks] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all data in parallel
        const [logosRes, agencyRes, servicesRes, socialRes, siteSettingsRes] = await Promise.all([
          api.get('/cms/logos').catch(() => ({ data: null })),
          api.get('/settings/agency-info').catch(() => ({ data: null })),
          api.get('/website/services').catch(() => ({ data: [] })),
          api.get('/cms/social-links').catch(() => ({ data: null })),
          api.get('/cms/site-settings').catch(() => ({ data: null }))
        ]);
        
        // Set logos/theme
        if (logosRes.data) {
          // Helper function to fix URLs from old domains
          const fixUrl = (url) => {
            if (!url) return null;
            if (url.includes('/api/uploads/')) {
              const match = url.match(/\/api\/uploads\/[^\s"']+/);
              if (match) return `${window.location.origin}${match[0]}`;
            }
            if (url.startsWith('http')) return url;
            return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
          };
          
          setThemeSettings({
            logo: {
              lightUrl: logosRes.data.logo_light ? fixUrl(logosRes.data.logo_light) : null,
            },
            agency: {
              name: logosRes.data.logo_alt || "MSC L'AGENCE"
            }
          });
        }
        
        // Set agency info (for contact info)
        if (agencyRes.data) {
          setAgencyInfo(agencyRes.data);
        }
        
        // Set services
        if (servicesRes.data?.length > 0) {
          const mappedServices = servicesRes.data.map(svc => ({
            id: svc.slug || svc.id,
            shortTitle: svc.short_name || svc.name || svc.title,
          }));
          setCmsServices(mappedServices);
        }
        
        // Set social links
        if (socialRes.data) {
          setSocialLinks(socialRes.data);
        }
        if (siteSettingsRes.data) {
          setSiteSettings(siteSettingsRes.data);
        }
      } catch (e) {
        console.log('Footer data not loaded');
      }
    };
    fetchData();
  }, []);

  // Use CMS data or fallback to static
  const displayServices = cmsServices || DEFAULT_SERVICES;
  const displayContact = {
    phone: agencyInfo?.phone || AGENCY_CONTACT.phone,
    email: agencyInfo?.email || AGENCY_CONTACT.email,
    address: agencyInfo?.address || AGENCY_CONTACT.address,
    city: `${agencyInfo?.postal_code || '13800'} ${agencyInfo?.city || 'Istres'}`
  };

  return (
    <footer className="py-16 border-t bg-slate-900 border-slate-800">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-5 gap-8 lg:gap-12 mb-12">
          {/* Company Info */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              {themeSettings?.logo?.lightUrl ? (
                <img 
                  src={themeSettings.logo.lightUrl} 
                  alt={themeSettings.agency?.name || "Logo"}
                  className="h-14 w-auto object-contain brightness-0 invert"
                />
              ) : (
                <div className={cn(
                  "w-14 h-14 rounded-full flex items-center justify-center border-2 shadow-lg",
                  "bg-gradient-to-br from-[#0f172a] to-[#1e3a5f] border-slate-600"
                )}>
                  <span className="font-bold text-lg text-white">MSC</span>
                </div>
              )}
              <div>
                <p className="font-bold text-white text-lg">{themeSettings?.agency?.name || "MSC L'AGENCE"}</p>
                <p className="text-sm text-slate-400">Mon Service Com</p>
              </div>
            </div>
            <p className="text-slate-400 text-sm mb-6">
              Votre partenaire communication à Istres depuis 2012. Création web, identité visuelle, production vidéo et marketing digital.
            </p>
            
            {/* Social Links - Only show if URL is configured */}
            <div className="flex items-center gap-3">
              {socialLinks?.facebook?.trim() && (
                <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-slate-800 hover:bg-cyan-500 flex items-center justify-center transition-all">
                  <Facebook className="w-5 h-5 text-white" />
                </a>
              )}
              {socialLinks?.instagram?.trim() && (
                <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-slate-800 hover:bg-cyan-500 flex items-center justify-center transition-all">
                  <Instagram className="w-5 h-5 text-white" />
                </a>
              )}
              {socialLinks?.linkedin?.trim() && (
                <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-slate-800 hover:bg-cyan-500 flex items-center justify-center transition-all">
                  <Linkedin className="w-5 h-5 text-white" />
                </a>
              )}
              {socialLinks?.youtube?.trim() && (
                <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-slate-800 hover:bg-cyan-500 flex items-center justify-center transition-all">
                  <Youtube className="w-5 h-5 text-white" />
                </a>
              )}
              {socialLinks?.twitter?.trim() && (
                <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-slate-800 hover:bg-cyan-500 flex items-center justify-center transition-all">
                  <Twitter className="w-5 h-5 text-white" />
                </a>
              )}
              {socialLinks?.tiktok?.trim() && (
                <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-slate-800 hover:bg-cyan-500 flex items-center justify-center transition-all">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
              )}
            </div>
          </div>
          
          {/* Services */}
          <div>
            <h4 className="font-semibold text-white mb-4">Services</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              {displayServices.map(s => (
                <li key={s.id}>
                  <Link to={`/site/${s.id}`} className="hover:text-cyan-400 transition-colors">{s.shortTitle}</Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Agency */}
          <div>
            <h4 className="font-semibold text-white mb-4">L&apos;agence</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><Link to="/site/equipe" className="hover:text-white transition-colors">Notre équipe</Link></li>
              <li><Link to="/site/realisations" className="hover:text-white transition-colors">Réalisations</Link></li>
              <li><Link to="/site#contact" className="hover:text-white transition-colors">Contact</Link></li>
            </ul>
            
            {/* Free Tools */}
            <h4 className="font-semibold text-white mb-3 mt-6">Outils gratuits</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li>
                <Link to="/site/timeline-communication" className="flex items-center gap-2 hover:text-cyan-400 transition-colors">
                  <Timer className="w-4 h-4" />
                  Timeline de la Com&apos;
                </Link>
              </li>
              {siteSettings?.game_section?.enabled && (
              <li>
                <Link to="/site/jeux-concours" className="flex items-center gap-2 hover:text-amber-400 transition-colors">
                  <Trophy className="w-4 h-4 text-amber-500" />
                  Jeux Concours
                </Link>
              </li>
              )}
            </ul>
          </div>
          
          {/* Contact */}
          <div>
            <h4 className="font-semibold text-white mb-4">Contact</h4>
            <ul className="space-y-3 text-sm text-slate-400">
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-cyan-400" />
                {displayContact.phone}
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-cyan-400" />
                {displayContact.email}
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-cyan-400 mt-0.5" />
                <span>{displayContact.address}<br/>{displayContact.city}<br/><em className="text-slate-500 text-xs not-italic">Siège social — Ne reçoit pas de public</em></span>
              </li>
            </ul>
            
            {/* Resources */}
            <h4 className="font-semibold text-white mb-3 mt-6">Ressources</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><Link to="/site/blog" className="hover:text-white transition-colors">Blog</Link></li>
              <li><Link to="/site/aide" className="hover:text-white transition-colors">Base de connaissances</Link></li>
              <li><Link to="/site/cgv" className="hover:text-white transition-colors">CGV</Link></li>
            </ul>
          </div>
        </div>
        
        {/* Bottom Footer */}
        <div className="border-t border-slate-800 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} MSC L&apos;Agence. Tous droits réservés.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-slate-500">
              <Link to="/site/mentions-legales" className="hover:text-white transition-colors">Mentions légales</Link>
              <span className="hidden md:inline">•</span>
              <Link to="/site/confidentialite" className="hover:text-white transition-colors">Politique de confidentialité</Link>
              <span className="hidden md:inline">•</span>
              <Link to="/site/cgv" className="hover:text-white transition-colors">CGV</Link>
            </div>
            <p className="text-xs text-slate-600">
              v{APP_VERSION} • {BUILD_DATE}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============================================
// SHARED LAYOUT WRAPPER
// ============================================
const SharedLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />
      <main className="pt-16 lg:pt-20">
        {children}
      </main>
      <SiteFooter />
    </div>
  );
};

export default SharedLayout;
